import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DialogTitle from "@mui/material/DialogTitle";
import LoadingButton from "@mui/lab/LoadingButton";
import DialogContent from "@mui/material/DialogContent";
import { BlobProvider } from "@react-pdf/renderer";
import React, { useEffect, useState } from "react";
import { TipeStruk } from "../../constants/types";
import useUkm from "../../services/queries/useUkm";
import useUser from "../../services/queries/useUser";
import toRupiah from "../../utils/toRupiah";
import { getLocalUkmId } from "../../utils/handleChangeUkmId";
import PrintStrukPdf from "../PrintStruk/PrintStrukPdf";
import { strukDataFromInvoice } from "../PrintStruk/strukData";
import useFindCustomStruk from "../../services/queries/useFindCustomStruk";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import PrintStrukA4 from "../PrintStrukA4/PrintStrukA4";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import { Tooltip, Skeleton } from "@mui/material";
import truncate from "../../utils/truncate";
import useDetailInvoice from "../../services/queries/useDetailInvoice";
import toTitleCase from "../../utils/toTitleCase";
import moment from "moment";
import "moment/locale/id";
import hexToRGBA from "../../utils/hexToRGBA";
import { MAIN_COLOR, textPrimary } from "../../constants/colors";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import useMobileBackClose from "../../hooks/useMobileBackClose";

interface IModalDetailInvoiceProps {
    id: number;
    fullScreen: boolean;
    isOpenModalDetailInvoice: boolean;
    closeModalDetailInvoice: () => void;
}

const ModalDetailInvoice = ({
    id,
    fullScreen,
    isOpenModalDetailInvoice,
    closeModalDetailInvoice,
}: IModalDetailInvoiceProps) => {
    useMobileBackClose(isOpenModalDetailInvoice, closeModalDetailInvoice);

    const theme = useTheme();
    const isPhoneScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const isTabletScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

    const { data: user } = useUser();
    const initialParamsUkm = {
        size: 500,
        page: 1,
        search: "",
    };
    const { data: ukm } = useUkm(initialParamsUkm);
    const ukmId = React.useMemo(() => {
        if (user?.userId) {
            return getLocalUkmId(user?.userId);
        }
        return null;
    }, [user?.userId]);

    const ukmData = React.useMemo(() => {
        if (ukmId) {
            return ukm?.content.find((li) => li.id === ukmId);
        }
        return ukm?.content[0];
    }, [ukm?.content, ukmId]);

    const { data: findCustomStruk } = useFindCustomStruk(Number(ukmId));
    const [kirimEmail, setKirimEmail] = useState(false);
    const [email, setEmail] = useState("");
    const [tipeStruk, setTipeStruk] = useState<string | null>(
        localStorage.getItem("tipeStrukLaporan") ?? TipeStruk.Mobile,
    );

    const [isIncludeDapur, setIsIncludeDapur] = useState<boolean>(
        localStorage.getItem("isIncludeDapur") === "true",
    );

    const handleKirimEmail = (checked: boolean) => {
        if (checked) {
            setEmail("");
            setKirimEmail(true);
        } else {
            setKirimEmail(false);
        }
    };

    const handleTipeStruk = (
        _event: React.MouseEvent<HTMLElement>,
        newTipeStruk: TipeStruk | null,
    ) => {
        if (newTipeStruk !== null) {
            setTipeStruk(newTipeStruk);
            localStorage.setItem("tipeStrukLaporan", newTipeStruk);

            const isDapur =
                newTipeStruk === TipeStruk.MobileDapur ||
                newTipeStruk === TipeStruk.Mobile80Dapur;
            setIsIncludeDapur(isDapur);
            localStorage.setItem("isIncludeDapur", String(isDapur));
        }
    };

    useEffect(() => {
        if (ukmData) {
            setTipeStruk(ukmData.webUkuranStruk);
            setKirimEmail(ukmData.webKirimStruk === 1);
            const isDapur =
                ukmData.webUkuranStruk === TipeStruk.MobileDapur ||
                ukmData.webUkuranStruk === TipeStruk.Mobile80Dapur;
            setIsIncludeDapur(isDapur);
        }
    }, [ukmData]);

    const [kategoriParams, setKategoriParams] = useState<{ grupProduk: 0 | 1 }>(
        {
            grupProduk: 0,
        },
    );

    // State untuk loading kategori
    const [isLoadingKategori, setIsLoadingKategori] = useState(false);

    const {
        data: detailInvoice,
        isLoading: isLoadingDetail,
        isFetching,
    } = useDetailInvoice(Number(id), { grupProduk: kategoriParams.grupProduk });

    const groupByKategori = false;

    const getFontSize = (desktop: number, tablet: number, mobile: number) => {
        if (isPhoneScreen) return mobile;
        if (isTabletScreen) return tablet;
        return desktop;
    };

    const handleCheckboxKategori = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const newValue = event.target.checked ? 1 : 0;
        setIsLoadingKategori(true);
        setKategoriParams((prev) => ({
            ...prev,
            grupProduk: newValue,
        }));
    };

    // Reset loading kategori ketika data selesai fetch
    useEffect(() => {
        if (!isFetching && isLoadingKategori) {
            setIsLoadingKategori(false);
        }
    }, [isFetching, isLoadingKategori]);

    const renderSkeletonItems = () => {
        // const skeletonCount = isPhoneScreen ? 3 : 5;
        const skeletonCount = 1;
        return (
            <Stack spacing={2} mt={2}>
                {Array.from({ length: skeletonCount }).map((_, idx) => (
                    <Stack
                        key={idx}
                        direction="row"
                        spacing={2}
                        alignItems="center"
                    >
                        <Skeleton variant="text" width="40%" height={24} />
                        <Skeleton variant="text" width="10%" height={24} />
                        <Skeleton
                            variant="text"
                            width="20%" height={24}
                            sx={{ marginLeft: "auto" }}
                        />
                    </Stack>
                ))}
            </Stack>
        );
    };

    const renderSkeletonKategori = () => {
        return (
            <Stack spacing={3} mt={2}>
                {Array.from({ length: 1 }).map((_, idx) => (
                    <Stack key={idx} spacing={1}>
                        <Skeleton variant="text" width="30%" height={28} />
                        {Array.from({ length: 1 }).map((_, itemIdx) => (
                            <Stack
                                key={itemIdx}
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                ml={2}
                            >
                                <Skeleton
                                    variant="text"
                                    width="35%"
                                    height={20}
                                />
                                <Skeleton
                                    variant="text"
                                    width="8%"
                                    height={20}
                                />
                                <Skeleton
                                    variant="text"
                                    width="18%"
                                    height={20}
                                    sx={{ marginLeft: "auto" }}
                                />
                            </Stack>
                        ))}
                    </Stack>
                ))}
            </Stack>
        );
    };

    useEffect(() => {
        if (!isOpenModalDetailInvoice) {
            setKategoriParams({ grupProduk: 0 });
            setKirimEmail(false);
            setEmail("");
            setIsLoadingKategori(false);
        }
    }, [isOpenModalDetailInvoice]);

    const handleCloseModal = () => {
        setKirimEmail(false);
        setEmail("");
        setKategoriParams({ grupProduk: 0 });
        setIsLoadingKategori(false);
        closeModalDetailInvoice();
    };

    const showSkeleton =
        isLoadingDetail || (isFetching && kategoriParams.grupProduk === 1);

    return (
        <Dialog
            maxWidth="md"
            fullScreen={fullScreen}
            fullWidth={true}
            open={isOpenModalDetailInvoice}
            scroll="body"
            onClose={handleCloseModal}
            PaperProps={{
                sx: {
                    borderRadius: isPhoneScreen ? 0 : 1,
                },
            }}
        >
            <DialogTitle>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                >
                    <Stack
                        direction="column"
                        spacing={0.5}
                        sx={{ flex: 1, minWidth: 0 }}
                    >
                        <Typography
                            fontSize={isPhoneScreen ? 18 : 20}
                            fontWeight="bold"
                        >
                            Detail Invoice
                        </Typography>

                        <Typography
                            fontSize={isPhoneScreen ? 12 : 14}
                            color="text.secondary"
                        >
                            Lihat rincian transaksi, daftar barang, dan
                            ringkasan pembayaran.
                        </Typography>
                    </Stack>

                    <IconButton
                        onClick={() => {
                            setKirimEmail(false);
                            setEmail("");
                            closeModalDetailInvoice();
                        }}
                        size={isPhoneScreen ? "small" : "medium"}
                        sx={{ flexShrink: 0 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent
                sx={{
                    paddingX: isPhoneScreen ? "12px 16px" : "16px 24px",
                    pb: 0,
                    flex: 1,
                    overflowY: "auto",
                }}
            >
                {detailInvoice?.meja?.ruangan && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            flexGrow: 1,
                            paddingTop: isPhoneScreen ? 1 : 2,
                            paddingBottom: isPhoneScreen ? 1 : 2,
                            mx: isPhoneScreen ? "-12px" : "-24px",
                            mb: isPhoneScreen ? "12px" : "20px",
                            backgroundColor: ukmData?.warna
                                ? hexToRGBA(ukmData?.warna, 0.24)
                                : hexToRGBA(MAIN_COLOR, 0.24),
                        }}
                    >
                        <Typography
                            variant="body2"
                            textAlign="center"
                            fontWeight={500}
                            fontSize={isPhoneScreen ? 13 : 14}
                        >
                            Nomor Meja:{" "}
                            {`${detailInvoice?.meja?.ruangan || ""}-${
                                detailInvoice?.meja?.nomor || ""
                            }`}
                        </Typography>
                        {detailInvoice?.pelangganWebstore && (
                            <Typography
                                variant="body2"
                                textAlign="center"
                                fontSize={isPhoneScreen ? 12 : 14}
                            >
                                Nama pelanggan:{" "}
                                {detailInvoice.pelangganWebstore.namaPelanggan}
                            </Typography>
                        )}
                    </Box>
                )}

                <Grid container spacing={isPhoneScreen ? 1 : 2}>
                    <Grid item xs={6} md={4}>
                        <Typography
                            color={textPrimary.light}
                            fontSize={getFontSize(13, 12, 11)}
                        >
                            Tanggal
                        </Typography>
                        <Typography
                            color={textPrimary.body}
                            fontSize={getFontSize(14, 13, 12)}
                        >
                            {detailInvoice?.tanggalTransaksi
                                ? moment(detailInvoice.tanggalTransaksi).format(
                                      "DD MMM YYYY HH:mm",
                                  )
                                : "-"}
                        </Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                        <Typography
                            color={textPrimary.light}
                            fontSize={getFontSize(13, 12, 11)}
                        >
                            No. Invoice
                        </Typography>
                        <Typography
                            color={textPrimary.body}
                            fontSize={getFontSize(14, 13, 12)}
                        >
                            {detailInvoice?.nomorInvoice || "-"}
                        </Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                        <Typography
                            color={textPrimary.light}
                            fontSize={getFontSize(13, 12, 11)}
                        >
                            Metode
                        </Typography>
                        <Typography
                            color={textPrimary.body}
                            fontSize={getFontSize(14, 13, 12)}
                        >
                            {detailInvoice?.metode
                                ? detailInvoice.metode
                                : detailInvoice?.isWebstore
                                  ? "COD"
                                  : "Tunai"}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={isPhoneScreen ? 1 : 2} mt={1}>
                    <Grid item xs={6} md={4}>
                        <Typography
                            color={textPrimary.light}
                            fontSize={getFontSize(13, 12, 11)}
                        >
                            Nama Kasir
                        </Typography>
                        <Typography
                            color={textPrimary.body}
                            fontSize={getFontSize(14, 13, 12)}
                        >
                            {detailInvoice?.kasir || "-"}
                        </Typography>
                    </Grid>
                    {detailInvoice?.sales && (
                        <Grid item xs={6} md={4}>
                            <Typography
                                color={textPrimary.light}
                                fontSize={getFontSize(13, 12, 11)}
                            >
                                Nama Sales
                            </Typography>
                            <Typography
                                color={textPrimary.body}
                                fontSize={getFontSize(14, 13, 12)}
                            >
                                {detailInvoice.sales}
                            </Typography>
                        </Grid>
                    )}
                    {detailInvoice?.pelangganWebstore && (
                        <>
                            <Grid item xs={6} md={4}>
                                <Typography
                                    color={textPrimary.light}
                                    fontSize={getFontSize(13, 12, 11)}
                                >
                                    Nama Pelanggan
                                </Typography>
                                <Typography
                                    color={textPrimary.body}
                                    fontSize={getFontSize(14, 13, 12)}
                                >
                                    {
                                        detailInvoice.pelangganWebstore
                                            .namaPelanggan
                                    }
                                </Typography>
                            </Grid>
                            <Grid item xs={6} md={5}>
                                <Typography
                                    color={textPrimary.light}
                                    fontSize={getFontSize(13, 12, 11)}
                                >
                                    Nomor Pelanggan
                                </Typography>
                                <Typography
                                    color={textPrimary.body}
                                    fontSize={getFontSize(14, 13, 12)}
                                >
                                    {
                                        detailInvoice.pelangganWebstore
                                            .nomorPelanggan
                                    }
                                </Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography
                                    color={textPrimary.light}
                                    fontSize={getFontSize(13, 12, 11)}
                                >
                                    Pemilik Rekening
                                </Typography>
                                <Typography
                                    color={textPrimary.body}
                                    fontSize={getFontSize(14, 13, 12)}
                                >
                                    {detailInvoice.pelangganWebstore
                                        .pemilikRekening ?? "-"}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <Typography
                                    color={textPrimary.light}
                                    fontSize={getFontSize(13, 12, 11)}
                                >
                                    Rekening Pelanggan
                                </Typography>
                                <Typography
                                    color={textPrimary.body}
                                    fontSize={getFontSize(14, 13, 12)}
                                >
                                    {detailInvoice.pelangganWebstore
                                        .rekeningPelanggan ?? "-"}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} md={5}>
                                <Typography
                                    color={textPrimary.light}
                                    fontSize={getFontSize(13, 12, 11)}
                                >
                                    Alamat
                                </Typography>
                                <Typography
                                    color={textPrimary.body}
                                    fontSize={getFontSize(14, 13, 12)}
                                >
                                    {detailInvoice.pelangganWebstore
                                        .alamatPelanggan &&
                                        `${detailInvoice.pelangganWebstore.alamatPelanggan},`}{" "}
                                    {detailInvoice.pelangganWebstore
                                        .kelurahan &&
                                        `${toTitleCase(detailInvoice.pelangganWebstore.kelurahan)},`}{" "}
                                    {detailInvoice.pelangganWebstore
                                        .kecamatan &&
                                        `${toTitleCase(detailInvoice.pelangganWebstore.kecamatan)},`}{" "}
                                    {detailInvoice.pelangganWebstore.kota &&
                                        `${toTitleCase(detailInvoice.pelangganWebstore.kota)},`}{" "}
                                    {detailInvoice.pelangganWebstore.provinsi &&
                                        toTitleCase(
                                            detailInvoice.pelangganWebstore
                                                .provinsi,
                                        )}
                                </Typography>
                            </Grid>
                        </>
                    )}
                </Grid>

                <Box my={isPhoneScreen ? 1.5 : 2}>
                    <Divider sx={{ borderStyle: "dashed" }} />
                </Box>

                <Box display="flex" justifyContent="right" marginTop={1}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={kategoriParams.grupProduk === 1}
                                onChange={handleCheckboxKategori}
                                size={isPhoneScreen ? "small" : "medium"}
                            />
                        }
                        label="Group by Kategori"
                        componentsProps={{
                            typography: {
                                fontSize: isPhoneScreen ? "12px" : "14px",
                            },
                        }}
                        sx={{ height: "fit-content" }}
                    />
                </Box>

                {showSkeleton ? (
                    kategoriParams.grupProduk === 1 ? (
                        renderSkeletonKategori()
                    ) : (
                        renderSkeletonItems()
                    )
                ) : (
                    <>
                        {kategoriParams.grupProduk === 1 ? (
                            <Stack spacing={1}>
                                {detailInvoice?.daftarKategori.map(
                                    (kategori, kategoriIndex) => (
                                        <Stack key={kategoriIndex}>
                                            <Stack marginBottom={1}>
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight="bold"
                                                    fontSize={getFontSize(
                                                        16,
                                                        15,
                                                        14,
                                                    )}
                                                >
                                                    {truncate(
                                                        kategori.kategori,
                                                        20,
                                                    )}
                                                </Typography>
                                            </Stack>
                                            <Grid
                                                container
                                                marginLeft={
                                                    isPhoneScreen ? 0 : 2
                                                }
                                                spacing={
                                                    isPhoneScreen ? 0.5 : 0
                                                }
                                            >
                                                {kategori.items.map(
                                                    (item, itemIndex) => (
                                                        <React.Fragment
                                                            key={itemIndex}
                                                        >
                                                            <Grid item xs={6}>
                                                                <Tooltip
                                                                    title={
                                                                        item.namaItem
                                                                    }
                                                                >
                                                                    <Typography
                                                                        variant="subtitle1"
                                                                        fontWeight="500"
                                                                        fontSize={getFontSize(
                                                                            14,
                                                                            13,
                                                                            12,
                                                                        )}
                                                                    >
                                                                        {truncate(
                                                                            item.namaItem,
                                                                            isPhoneScreen
                                                                                ? 15
                                                                                : 20,
                                                                        )}
                                                                    </Typography>
                                                                </Tooltip>
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    color="#A1A1AA"
                                                                    fontSize={getFontSize(
                                                                        12,
                                                                        11,
                                                                        10,
                                                                    )}
                                                                >
                                                                    @
                                                                    {toRupiah(
                                                                        item.hargaSatuan,
                                                                    )}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={2}>
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    fontWeight="500"
                                                                    fontSize={getFontSize(
                                                                        14,
                                                                        13,
                                                                        12,
                                                                    )}
                                                                >
                                                                    {
                                                                        item.jumlahItem
                                                                    }
                                                                    x
                                                                </Typography>
                                                            </Grid>
                                                            <Grid
                                                                item
                                                                xs={4}
                                                                display="flex"
                                                                justifyContent="flex-end"
                                                                paddingRight={
                                                                    isPhoneScreen
                                                                        ? 0
                                                                        : 2
                                                                }
                                                            >
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    fontWeight="bold"
                                                                    textAlign="end"
                                                                    fontSize={getFontSize(
                                                                        14,
                                                                        13,
                                                                        12,
                                                                    )}
                                                                >
                                                                    {toRupiah(
                                                                        item.hargaSatuan *
                                                                            item.jumlahItem,
                                                                    )}
                                                                </Typography>
                                                            </Grid>
                                                        </React.Fragment>
                                                    ),
                                                )}
                                            </Grid>
                                        </Stack>
                                    ),
                                )}
                            </Stack>
                        ) : (
                            <Stack spacing={isPhoneScreen ? 1 : 2}>
                                {detailInvoice?.daftarItem.map(
                                    (items, index) => (
                                        <Stack direction="column" key={index}>
                                            <Grid
                                                container
                                                spacing={
                                                    isPhoneScreen ? 0.5 : 0
                                                }
                                            >
                                                <Grid item xs={6}>
                                                    <Tooltip
                                                        title={items.namaItem}
                                                    >
                                                        <Typography
                                                            color="#464E4B"
                                                            fontSize={getFontSize(
                                                                14,
                                                                13,
                                                                12,
                                                            )}
                                                        >
                                                            {truncate(
                                                                items.namaItem,
                                                                isPhoneScreen
                                                                    ? 15
                                                                    : 20,
                                                            )}
                                                        </Typography>
                                                    </Tooltip>
                                                    <Typography
                                                        mt={0.5}
                                                        fontSize={getFontSize(
                                                            12,
                                                            11,
                                                            10,
                                                        )}
                                                    >
                                                        {`@${toRupiah(items.hargaSatuan)}`}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <Typography
                                                        fontWeight="bold"
                                                        color="#464E4B"
                                                        fontSize={getFontSize(
                                                            14,
                                                            13,
                                                            12,
                                                        )}
                                                    >
                                                        {items.jumlahItem}x
                                                    </Typography>
                                                </Grid>
                                                <Grid
                                                    item
                                                    xs={4}
                                                    display="flex"
                                                    justifyContent="flex-end"
                                                >
                                                    <Typography
                                                        fontWeight="bold"
                                                        color="#464E4B"
                                                        textAlign="end"
                                                        fontSize={getFontSize(
                                                            14,
                                                            13,
                                                            12,
                                                        )}
                                                    >
                                                        {toRupiah(
                                                            items.hargaSatuan *
                                                                items.jumlahItem,
                                                        )}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Stack>
                                    ),
                                )}
                            </Stack>
                        )}
                    </>
                )}

                <Box
                    marginY={2}
                    padding={isPhoneScreen ? 1.5 : 2}
                    border={1}
                    borderColor="#d4d4d4"
                    borderRadius={2}
                >
                    <Typography
                        marginBottom={1.5}
                        fontSize={getFontSize(15, 14, 13)}
                        color="#000000"
                        fontWeight="bold"
                    >
                        Ringkasan Pembayaran
                    </Typography>

                    {[
                        {
                            label: "Subtotal Harga",
                            value: detailInvoice
                                ? toRupiah(detailInvoice.totalHarga)
                                : "0",
                            color: "text.primary",
                            bg: "transparent",
                            prefix: "Rp.",
                        },
                        {
                            label: detailInvoice?.serviceCustomPersen
                                ? `Service (${detailInvoice.serviceCustomPersen}%)`
                                : "Service",
                            value: detailInvoice?.serviceCustom
                                ? toRupiah(detailInvoice.serviceCustom)
                                : "0",
                            color: "#FB923C",
                            bg: "#FFF5EC",
                            prefix: "Rp.",
                        },
                        {
                            label: detailInvoice?.pajakPersen
                                ? `Pajak (${detailInvoice.pajakPersen}%)`
                                : "Pajak",
                            value: detailInvoice?.pajak
                                ? toRupiah(detailInvoice.pajak)
                                : "0",
                            color: "#FB923C",
                            bg: "#FFF5EC",
                            prefix: "Rp.",
                        },
                        {
                            label: "Diskon",
                            value: detailInvoice
                                ? `${toRupiah(detailInvoice.diskon)})`
                                : "0)",
                            color: "#FB923C",
                            bg: "transparent",
                            prefix: "(Rp.",
                        },
                    ].map(({ label, value, color, bg, prefix }, i) => (
                        <Box
                            key={i}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mt={i === 0 ? 0 : 1}
                        >
                            <Typography
                                fontSize={getFontSize(14, 13, 12)}
                                color="#404040"
                                sx={{ flex: 1 }}
                            >
                                {label}
                            </Typography>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                bgcolor={bg}
                                borderRadius={0.5}
                                px={1}
                                minWidth={isPhoneScreen ? 110 : 130}
                            >
                                <Typography
                                    fontSize={getFontSize(14, 13, 12)}
                                    color={color}
                                    fontWeight="bold"
                                    mr={1}
                                >
                                    {prefix}
                                </Typography>
                                <Typography
                                    fontSize={getFontSize(14, 13, 12)}
                                    color={color}
                                    fontWeight="bold"
                                >
                                    {value}
                                </Typography>
                            </Box>
                        </Box>
                    ))}

                    <Divider sx={{ borderStyle: "dashed", mt: 1.5, mb: 1 }} />

                    {[
                        {
                            label: "Total Tagihan",
                            value: detailInvoice
                                ? toRupiah(detailInvoice.totalPembayaran)
                                : "0",
                            color: "#45A779",
                            bg: "#E8F6ED",
                        },
                        {
                            label: "Uang yang Dibayar",
                            value: detailInvoice
                                ? toRupiah(detailInvoice.uangYangDibayar)
                                : "0",
                            color: "#FB923C",
                            bg: "#FFF5EC",
                        },
                        {
                            label: "Kembalian",
                            value: detailInvoice
                                ? toRupiah(detailInvoice.kembalian)
                                : "0",
                            color: "#DC2626",
                            bg: "#FCEAEA",
                        },
                    ].map(({ label, value, color, bg }, i) => (
                        <Box
                            key={i}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mt={i === 0 ? 0 : 1}
                        >
                            <Typography
                                fontSize={getFontSize(14, 13, 12)}
                                color="#404040"
                                sx={{ flex: 1 }}
                            >
                                {label}
                            </Typography>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                bgcolor={bg}
                                borderRadius={0.5}
                                px={1}
                                minWidth={isPhoneScreen ? 110 : 130}
                            >
                                <Typography
                                    fontSize={getFontSize(14, 13, 12)}
                                    color={color}
                                    fontWeight="bold"
                                    mr={1}
                                >
                                    Rp.
                                </Typography>
                                <Typography
                                    fontSize={getFontSize(14, 13, 12)}
                                    color={color}
                                    fontWeight="bold"
                                >
                                    {value}
                                </Typography>
                            </Box>
                        </Box>
                    ))}

                    {detailInvoice?.keterangan && (
                        <>
                            <Divider sx={{ borderStyle: "dashed", mt: 2 }} />
                            <Stack spacing={0.5} mt={1}>
                                <Typography
                                    fontSize={getFontSize(14, 13, 12)}
                                    color="#404040"
                                >
                                    Keterangan
                                </Typography>
                                <TextField
                                    disabled
                                    multiline
                                    rows={2}
                                    fullWidth
                                    value={detailInvoice.keterangan}
                                    sx={{
                                        "& .MuiOutlinedInput-input.Mui-disabled":
                                            {
                                                color: "rgba(0, 0, 0, 0.87)",
                                                WebkitTextFillColor:
                                                    "rgba(0, 0, 0, 0.87)",
                                            },
                                    }}
                                />
                            </Stack>
                        </>
                    )}
                </Box>

                {/* Struk */}
                <Stack direction="column" spacing={1}>
                    <Typography
                        fontSize={getFontSize(14, 13, 12)}
                        fontWeight={500}
                    >
                        Pilih Tipe Struk
                    </Typography>
                    <ToggleButtonGroup
                        value={tipeStruk}
                        exclusive
                        size="small"
                        onChange={handleTipeStruk}
                        sx={{
                            flexWrap: "wrap",
                            gap: isPhoneScreen ? 0.5 : 0,
                            ...(isPhoneScreen && {
                                "& .MuiToggleButtonGroup-grouped:not(:first-of-type)":
                                    {
                                        marginLeft: 0,
                                        borderLeft:
                                            "1px solid rgba(0, 0, 0, 0.12)",
                                        borderTopLeftRadius: 4,
                                        borderBottomLeftRadius: 4,
                                    },
                                "& .MuiToggleButtonGroup-grouped:not(:last-of-type)":
                                    {
                                        borderTopRightRadius: 4,
                                        borderBottomRightRadius: 4,
                                    },
                            }),
                        }}
                    >
                        <ToggleButton
                            value={TipeStruk.Mobile}
                            sx={{
                                textTransform: "none",
                                px: isPhoneScreen ? 1.5 : 2,
                            }}
                        >
                            <b>A8 58mm</b>
                        </ToggleButton>
                        <ToggleButton
                            value={TipeStruk.Mobile80}
                            sx={{
                                textTransform: "none",
                                px: isPhoneScreen ? 1.5 : 2,
                            }}
                        >
                            <b>A8 80mm</b>
                        </ToggleButton>
                        <ToggleButton
                            value={TipeStruk.PC}
                            sx={{
                                textTransform: "none",
                                px: isPhoneScreen ? 1.5 : 2,
                            }}
                        >
                            <b>A4</b>
                        </ToggleButton>
                        <ToggleButton
                            value={TipeStruk.MobileDapur}
                            sx={{
                                textTransform: "none",
                                px: isPhoneScreen ? 1.5 : 2,
                            }}
                        >
                            <b>A8 58mm Dapur</b>
                        </ToggleButton>
                        <ToggleButton
                            value={TipeStruk.Mobile80Dapur}
                            sx={{
                                textTransform: "none",
                                px: isPhoneScreen ? 1.5 : 2,
                            }}
                        >
                            <b>A8 80mm Dapur</b>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>

                <Box display="flex">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={kirimEmail}
                                size={isPhoneScreen ? "small" : "medium"}
                                onChange={(_, checked) => {
                                    handleKirimEmail(checked);
                                }}
                            />
                        }
                        label="Kirim struk"
                        componentsProps={{
                            typography: { fontSize: getFontSize(14, 13, 12) },
                        }}
                        sx={{ height: "fit-content" }}
                    />
                </Box>

                {kirimEmail && (
                    <Grid
                        container
                        alignItems="center"
                        spacing={1}
                        marginBottom={2}
                    >
                        <Grid item xs={12} md={6}>
                            <TextField
                                value={email}
                                size="small"
                                type="email"
                                fullWidth
                                placeholder="8316352725"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            +62
                                        </InputAdornment>
                                    ),
                                }}
                                onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                ) => setEmail(event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Button
                                disabled={email.length <= 0}
                                variant="outlined"
                                onClick={() => {
                                    setKirimEmail(false);
                                    setEmail("");
                                    closeModalDetailInvoice();
                                }}
                                href={`https://wa.me/62${email}?text=Struk%20pembayaran%20ULO%20${detailInvoice?.nomorInvoice || ""}%20${window.location.origin}/struk/${detailInvoice?.encrypt || ""}`}
                                target="_blank"
                                rel="noreferrer"
                                fullWidth
                            >
                                Kirim struk ke WhatsApp
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: 3 }}>
                <Stack
                    width="100%"
                    direction="column"
                    alignItems="center"
                    spacing={1}
                    mt={2}
                >
                    <Box width="100%">
                        {ukmData &&
                            detailInvoice &&
                            (tipeStruk === TipeStruk.Mobile ||
                            tipeStruk === TipeStruk.Mobile80 ||
                            tipeStruk === TipeStruk.MobileDapur ||
                            tipeStruk === TipeStruk.Mobile80Dapur ? (
                                <BlobProvider
                                    document={
                                        <PrintStrukPdf
                                            ukm={ukmData}
                                            data={strukDataFromInvoice(
                                                detailInvoice,
                                            )}
                                            customStruk={findCustomStruk}
                                            format={{
                                                is80mm:
                                                    tipeStruk ===
                                                        TipeStruk.Mobile80 ||
                                                    tipeStruk ===
                                                        TipeStruk.Mobile80Dapur,
                                                isIncludeDapur,
                                                groupByKategori,
                                            }}
                                        />
                                    }
                                >
                                    {({ blob, url, loading, error }) => {
                                        return (
                                            <LoadingButton
                                                loading={loading}
                                                loadingPosition="center"
                                                fullWidth
                                                variant="contained"
                                                href={url ?? ""}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Print Ulang Struk
                                            </LoadingButton>
                                        );
                                    }}
                                </BlobProvider>
                            ) : (
                                <BlobProvider
                                    document={
                                        <PrintStrukA4
                                            ukm={ukmData}
                                            data={strukDataFromInvoice(
                                                detailInvoice,
                                            )}
                                            customStruk={findCustomStruk}
                                            format={{ groupByKategori }}
                                        />
                                    }
                                >
                                    {({ blob, url, loading, error }) => {
                                        return (
                                            <LoadingButton
                                                loading={loading}
                                                loadingPosition="center"
                                                fullWidth
                                                variant="contained"
                                                href={url ?? ""}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Print Ulang Struk
                                            </LoadingButton>
                                        );
                                    }}
                                </BlobProvider>
                            ))}
                    </Box>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default ModalDetailInvoice;